import React, { useEffect, useState } from "react";
import { FieldErrorsImpl, useForm } from "react-hook-form";
import { Box, Flex, Link, Text } from "@chakra-ui/react";
import { AppInput } from "../../../components/AppInput";
import { FormError, SimpleFormError } from "../../../components/login/formError.component";
import { FormButton } from "../../../components/login/button.component";
import { postLogin } from "../../../_services/auth.service";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { TLocationHandler } from "../_types/location.type";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AccountRecovery } from "../AccountRecovery/AccountRecovery";
import { useCookies } from "react-cookie";
import { ILoginResponse } from "../../../_services/interface/login.interface";
import { authThrottleMessage } from "../../../utils/authThrottleMessage";
import { AUTH_SSO_ONLY, AUTH_SSO_ONLY_MESSAGE } from "../ssoOnly";
import { ValidationForm } from "../validation/ValidationForm";
import { IErrorResponse } from "../../../_services/interface/IDefaults";
import { AccountBlockForm } from "../AccountBlock/AccountBlock";
import useLoginToken from "../../../store/login.store";
import {
  logAuthDiag,
  persistSessionToken,
  readSessionFallback,
  tokenCookieOptions,
} from "../../../_services/authClient";
// GEN-3167: entrar por um link leva de volta AO LINK, não ao Dashboard.
import { destinoAposLogin } from "../../../_services/returnTo";
// GEN-4150: quem veio do broker de SSO tem a chave do cliente guardada para renovar a sessão depois.
import {
  clearSsoClient,
  getSsoClient,
  irParaBroker,
  syncSsoClientFromLogin,
  takeSsoReloginCompletion,
} from "../../../_services/ssoRelogin";

interface LoginFormProps {
  locationHandler: TLocationHandler;
}

export const LoginForm = (props: LoginFormProps) => {
  const navigate = useNavigate();

  //Base form structure
  const YupLoginForm = Yup.object().shape({
    email: Yup.string().email("O email precisa ser válido").required("Preencha o campo de email"),
    password: Yup.string().required("Preencha o campo de senha"),
  });

  //Generate o resolver
  const resolverForm = { resolver: yupResolver(YupLoginForm) };
  const [errorMessage, setErrorMessage] = useState<FieldErrorsImpl>();
  const [smplErrorMessage, setSmplErrorMessage] = useState<string>();
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm(resolverForm);
  const [cookies, setCookie] = useCookies(["token", "deviceCode"]);
  const { setToken } = useLoginToken();
  const [searchParams] = useSearchParams();
  // GEN-4150: cliente do broker de SSO de onde a última sessão veio (cookie `sso_client`). Lido uma
  // vez, no mount: é o que decide mostrar o atalho "Entrar via SSO" — aqui NÃO há redirect
  // automático (quem saiu de propósito tem que conseguir ficar fora; o Entra re-loga em silêncio).
  const [ssoClient] = useState<string | null>(() => getSsoClient());
  const [ssoRedirecting, setSsoRedirecting] = useState(false);

  const entrarViaSso = async () => {
    if (!ssoClient || ssoRedirecting || submitDisabled) return;
    setSsoRedirecting(true);
    // Mesma sequência do Logout (cooldown antes do await, URL só do backend, pendência, diag, assign).
    if (await irParaBroker(ssoClient, "sso-relogin:manual")) return;
    setSmplErrorMessage("Não foi possível abrir o acesso via SSO. Tente novamente ou procure o administrador.");
    setSsoRedirecting(false);
  };

  const handleFormInfo = async (data: any) => {
    // GEN-4150: com o redirect ao broker em andamento, um Enter no formulário iniciaria um login por
    // senha em paralelo (duas sessões, e o cookie `sso_client` apagado logo antes de ir ao broker).
    if (ssoRedirecting) return;
    //First the system will disable the login button
    setSubmitDisabled(true);
    //Send the information to the system

    let deviceCode = cookies.deviceCode;
    if (!deviceCode) {
      deviceCode = crypto.randomUUID();
      // Cria um cookie deviceCode que nunca expira (definindo um tempo de expiração para 10 anos, por exemplo)
      const expirationDate = new Date();
      expirationDate.setFullYear(expirationDate.getFullYear() + 10);
      setCookie("deviceCode", deviceCode, { path: "/", expires: expirationDate });
    }

    const { status, response } = await postLogin({ ...data, deviceCode });

    if (status === 200) {
      if ((response as any).waitingValidation) {
        //This user still needs to validate its user
        props.locationHandler(
          <ValidationForm
            locationHandler={props.locationHandler}
            email={data.email}
            userid={(response as any).guid_user}
          />
        );
      } else {
        //Set the cookie
        const theToken = (response as ILoginResponse).accessToken;
        setToken(theToken);
        // clear() ANTES do persist — senão apagaria o fallback recém-gravado
        localStorage.clear();
        setCookie("token", theToken, tokenCookieOptions());
        persistSessionToken(theToken);
        // GEN-4150: sessão por senha não veio do broker — o cookie `sso_client` tem que dizer de onde
        // veio a ÚLTIMA entrada, senão outra pessoa no mesmo navegador seria mandada ao IdP alheio.
        clearSsoClient();
        logAuthDiag("login-password:after-set-cookie", {
          email: data.email,
          tokenLen: theToken?.length ?? 0,
          lsFallbackOk: readSessionFallback() === theToken,
        });
        navigate(destinoAposLogin());
      }
    } else {
      const errorResponse = response as IErrorResponse;
      const err = errorResponse.message;
      switch (err) {
        case "LOGIN_USER_NOT_FOUND":
          setSmplErrorMessage("Usuário inválido ou não encontrado");
          break;
        case "LOGIN_USER_BLOCKED":
          props.locationHandler(<AccountBlockForm locationHandler={props.locationHandler} email={data.email} />);
          break;
        case "LOGIN_ACCOUNT_IS_BLOCKED":
          props.locationHandler(<AccountBlockForm locationHandler={props.locationHandler} email={data.email} />);
          break;
        case "LOGIN_USER_INACTIVE":
          setSmplErrorMessage("Usuário inválido ou não encontrado");
          break;
        case "LOGIN_INVALID_COMPANY":
          setSmplErrorMessage("Este usuário é parte de uma empresa inválida");
          break;
        case "LOGIN_WRONG_PASSWORD":
          setSmplErrorMessage("Email ou Senha são inválidos.");
          break;
        case "LOGIN_SSO_GATEWAY":
          // GEN-4150: quando sabemos de onde a pessoa veio, apontar o caminho em vez de só negar.
          setSmplErrorMessage(
            ssoClient
              ? "Esta empresa apenas aceita acesso via SSO. Use o link 'Entrar via SSO' abaixo."
              : "Esta empresa apenas aceita acesso via SSO."
          );
          break;
        // GEN-3604: 403 para conta que só entra pela Microsoft — não é senha errada, é o botão errado.
        case AUTH_SSO_ONLY:
          setSmplErrorMessage(AUTH_SSO_ONLY_MESSAGE);
          break;
        // GEN-3039: 429 de limite de tentativas. Sem estes dois cases o erro cairia no `default`
        // ("Email ou Senha sao invalidos.") e o usuario bloqueado tentaria de novo, reacendendo o
        // bloqueio sem nunca entender o motivo.
        case "AUTH_TOO_MANY_ATTEMPTS":
        case "LOGIN_TEMPORARILY_LOCKED":
          setSmplErrorMessage(authThrottleMessage(errorResponse.retryAfter));
          break;
        default:
          setSmplErrorMessage("Email ou Senha são inválidos.");
          break;
      }
    }

    //Return the state of the button
    setSubmitDisabled(false);
  };

  const pagePasswordReset = () => {
    props.locationHandler(<AccountRecovery locationHandler={props.locationHandler} />);
  };

  useEffect(() => {
    // 1) Se veio token na URL, assume logado
    const urlToken = searchParams.get("token");
    if (urlToken) {
      setToken(urlToken);
      // clear() ANTES do persist — senão apagaria o fallback recém-gravado
      localStorage.clear();
      setCookie("token", urlToken, tokenCookieOptions());
      persistSessionToken(urlToken);
      // GEN-4150: o backend manda `&sso=<key>` quando o login veio do broker de um cliente com
      // relogin habilitado. Guardado em cookie durável (não no localStorage que acabou de ser
      // limpo) para, quando ESTA sessão expirar, voltar ao broker em vez da tela de senha.
      // Sem `&sso=` (cliente sem relogin habilitado, ou token de outra origem) a chave antiga é APAGADA.
      syncSsoClientFromLogin(searchParams.get("sso"));
      const ssoClientGravado = getSsoClient() ?? undefined;
      // Se há um redirect ao broker pendente nesta aba, este token é a VOLTA dele: é o evento que
      // prova, em produção, que a renovação funcionou (a Taxly não tem conta nos IdPs dos clientes).
      // Consumido aqui — um redirect gera no máximo um `completed`.
      const elapsedMs = takeSsoReloginCompletion();
      logAuthDiag("login-sso:after-set-cookie-from-url", {
        tokenLen: urlToken.length,
        lsFallbackOk: readSessionFallback() === urlToken,
        ssoClient: ssoClientGravado,
      });
      if (elapsedMs !== null) {
        logAuthDiag("sso-relogin:completed", { ssoClient: ssoClientGravado, elapsedMs });
      }

      // replace=true remove essa URL (com token) do histórico
      navigate(destinoAposLogin(), { replace: true });
      return;
    }

    // 2) Se já tem sessão (cookie ou fallback), manda pro dashboard
    if (cookies.token || readSessionFallback()) {
      logAuthDiag("login-page:already-has-cookie-redirect", {
        viaFallback: !cookies.token,
      });
      navigate(destinoAposLogin(), { replace: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Voltar do broker pelo histórico pode restaurar esta página do bfcache com `ssoRedirecting`
    // ligado — o link ficaria em "Abrindo SSO…" para sempre.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setSsoRedirecting(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(() => {
    if (errors) {
      setErrorMessage(errors as any);
    }
  }, [errors]);

  return (
    <Flex display="flex" fontFamily="Poppins" fontWeight={500} alignItems="center" width={369}>
      <Flex direction="column" w="100%">
        <Text fontSize="32px" textAlign="center">
          Login
        </Text>
        <Box marginTop="50px">
          {errorMessage ? <FormError message={errorMessage} /> : null}
          {smplErrorMessage ? <SimpleFormError msg={smplErrorMessage} /> : null}

          <form
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleSubmit(handleFormInfo)();
              }
            }}
          >
            <Flex direction="column" gap="16px" alignItems="stretch">
              <AppInput placeholder="Seu Email" type="email" {...register("email")} />
              <AppInput placeholder="Sua Senha" type="password" {...register("password", { required: true })} />

              <FormButton onClick={handleSubmit(handleFormInfo)} disabled={submitDisabled || ssoRedirecting}>
                Entrar
              </FormButton>

              <Box marginTop="30px" borderBottom="1px solid #DBDBDB" />
              <Flex justifyContent="center" alignItems="center" fontSize="12px" marginTop="-10px">
                <Link onClick={pagePasswordReset} _hover={{ color: "#8789FF", cursor: "pointer" }} color="#4B4EFF">
                  Esqueci minha senha
                </Link>
                {/* GEN-4150: atalho para quem entra pelo broker de SSO do cliente (cookie `sso_client`). */}
                {ssoClient ? (
                  <>
                    <Text mx="8px" color="#DBDBDB">
                      |
                    </Text>
                    <Link
                      onClick={entrarViaSso}
                      aria-disabled={ssoRedirecting}
                      _hover={{ color: "#8789FF", cursor: ssoRedirecting ? "wait" : "pointer" }}
                      color="#4B4EFF"
                    >
                      {ssoRedirecting ? "Abrindo SSO…" : "Entrar via SSO"}
                    </Link>
                  </>
                ) : null}
                {/* <Link _hover={{ color: "#8789FF", cursor: "pointer" }} color="#4B4EFF">
                  Preciso de ajuda
                </Link> */}
              </Flex>
            </Flex>
          </form>
        </Box>
      </Flex>
    </Flex>
  );
};
